import { BreakpointObserver, type BreakpointState } from '@angular/cdk/layout';
import { ViewportRuler } from '@angular/cdk/overlay';
import { inject, Injectable, Injector, signal, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, shareReplay } from 'rxjs/operators';
import { MediaBreakpointsEnum } from '../enums/media-breakpoints.enum';
import { type ElementSize } from '../models/element-size.model';

@Injectable({
    providedIn: 'root',
})
export class BreakpointService {
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly injector = inject(Injector);
    private readonly viewportRuler = inject(ViewportRuler);
    public readonly isMobile = this.formatResult(MediaBreakpointsEnum.mdMediaDown);
    public readonly isTablet = this.formatResult(MediaBreakpointsEnum.mdMediaOnly);
    public readonly isDesktop = this.formatResult(MediaBreakpointsEnum.lgMediaUp);
    public readonly isRetina = this.formatResult(MediaBreakpointsEnum.retina);

    public readonly topBarSize = signal<ElementSize>({
        width: 0,
        height: 0,
    });

    public readonly headerSize = signal<ElementSize>({
        width: 0,
        height: 0,
    });

    public readonly viewPortSize = toSignal(this.viewportRuler.change().pipe(
        map((res: Event): ElementSize => {
            const target: Window = res.target as Window;

            return {
                height: target.innerHeight,
                width: target.innerWidth,
            };
        }),
    ), { initialValue: this.viewportRuler.getViewportSize() as ElementSize  });

    private readonly _scrollPositionYSubject = signal(this.viewportRuler.getViewportScrollPosition().top);

    public scrollPositionY(): Signal<number> {
        const scrollY: number = this.viewportRuler.getViewportScrollPosition().top;

        this._scrollPositionYSubject.set(scrollY);

        return this._scrollPositionYSubject;
    }

    private formatResult(breakpoint: string): Signal<boolean> {
        return toSignal(this.breakpointObserver.observe(breakpoint).pipe(
            map((result: BreakpointState): boolean => result.matches),
            shareReplay({ refCount: true }),
        ), { initialValue: false, injector: this.injector });
    }
}
